import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import API, { UPLOADS_URL } from '../api/axios';
import PageBanner from '../components/PageBanner';

const DEFAULT_CONTENT =
  "<p>St. Andrews World School, Siddharth Vihar offers a thoughtfully designed campus that balances academic spaces, recreational areas, and creative zones. Every facility has been planned to support the physical, intellectual, and artistic growth of our students, while ensuring safety, hygiene, and comfort throughout the school.</p>";

const InfrastructurePage = () => {
  const pageData = usePage('infrastructure', { content: DEFAULT_CONTENT });
  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const res = await API.get('/facilities/public');
        setFacilities(res.data?.data || res.data || []);
      } catch (err) {
        console.error("Error fetching facilities:", err);
      }
    };
    fetchFacilities();
  }, []);

  const title = pageData.title || 'Infrastructure';

  return (
    <>
      <SEO slug="infrastructure" title={title} description="Infrastructure of St. Andrews World School, Siddharth Vihar." keywords="Infrastructure, SAWS Siddharth Vihar" />

      <PageBanner 
        title={pageData.bannerHeading || title} 
        subtitle={pageData.bannerSubHeading || ""} 
        breadcrumbs={[
          { label: 'Home', link: '/' }, 
          { label: 'About Us', link: '/about-us' },
          { label: title }
        ]} 
        backgroundImage={pageData.bannerImage} 
        fallback={`${process.env.PUBLIC_URL}/images/page-banners/curricullam-infrastructure.jpg`} 
      />

      <section className="section bg-white">
        <div className="container mx-auto px-[15px]">
          {/* Main Content */}
          <div className="card p-7 md:p-10 mb-12 shadow-sm border border-gray-100">
            <div className="prose max-w-none text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: pageData.content || DEFAULT_CONTENT }} />
          </div>

          {/* Infrastructure Photos Grid */}
          {facilities.length > 0 && (
            <div>
             
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                {facilities.map((facility, idx) => (
                  <Reveal key={idx} delayMs={idx * 50}>
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition hover:-translate-y-1 hover:shadow-md group">
                      <div className="aspect-[4/3] overflow-hidden bg-gray-50 flex items-center justify-center relative">
                        {facility.image ? (
                          <img 
                            src={`${UPLOADS_URL}/${facility.image}`} 
                            alt={facility.title} 
                            onError={(e) => {
                              // Fallback if image isn't in facilities/ folder
                              e.target.onerror = null; 
                              e.target.src = `${UPLOADS_URL}/${facility.image}`;
                            }}
                            className="w-full h-full object-cover transition duration-500 group-hover:scale-110"
                          />
                        ) : (
                          <i className="fas fa-building text-6xl text-gray-200"></i>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-90"></div>
                        <h3 className="absolute bottom-4 left-6 right-6 font-bold text-xl text-white drop-shadow-md">{facility.title}</h3>
                      </div>
                      {facility.description && (
                         <div className="p-6 text-gray-600 text-sm">
                           <div dangerouslySetInnerHTML={{ __html: facility.description }} />
                         </div>
                      )}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default InfrastructurePage;
